import { groupColors } from "@/configs/map/groups";
import { useActiveDataflowId } from "@/stores/dataflow";
import { useMapStore } from "@/stores/map";
import type { GroupState } from "@/types/stores/map";
import { useMemo } from "react";

/**
 * Unified processed group - treats chunks and groups identically
 */
export interface ProcessedGroup {
  type: "group" | "chunk";
  id: string;
  label: string;
  values: string[]; // For chunks: multiple values; for groups: single value array
  color: string;
  shape?: "circle" | "square" | "triangle" | "star" | "diamond";
  displayName?: string;
  from?: string; // For numeric chunk ranges
  to?: string; // For numeric chunk ranges
}

/**
 * Hook to process groups and chunks into a unified format
 * Returns groups and chunks with identical structure - chunks are just groups with multiple values
 * 
 * @param groupColumnName - Column name to group by
 * @param uniqueValues - Array of unique values in the column
 * @param groupState - Legacy group state (for backward compatibility)
 * @returns Array of processed groups/chunks in unified format
 */
export function useProcessedGroups(
  groupColumnName: string | null,
  uniqueValues: string[],
  groupState: GroupState | undefined
): ProcessedGroup[] {
  const activeDataflowId = useActiveDataflowId();
  const unifiedGroups = useMapStore((state) => 
    activeDataflowId ? state.unifiedGroups[activeDataflowId] : undefined
  );

  const chunks = groupState?.chunks;
  const groupConfigs = groupState?.groupConfigs;
  const chunkConfigs = groupState?.chunkConfigs;
  const visualizationMode = groupState?.visualizationMode;
  const visualizationLayerType = groupState?.visualizationLayerType;

  // Create stable keys for configs to prevent unnecessary recalculations
  const groupConfigsKey = useMemo(() => {
    if (!groupConfigs) return "";
    return Object.keys(groupConfigs)
      .sort()
      .map(
        (k) =>
          `${k}:${groupConfigs[k]?.color || ""}:${groupConfigs[k]?.shape || ""}`
      )
      .join(",");
  }, [groupConfigs]);

  const chunkConfigsKey = useMemo(() => {
    if (!chunkConfigs) return "";
    return Object.keys(chunkConfigs)
      .sort()
      .map(
        (k) =>
          `${k}:${chunkConfigs[k]?.color || ""}:${
            chunkConfigs[k]?.shape || ""
          }:${chunkConfigs[k]?.displayName || ""}:${
            chunkConfigs[k]?.from || ""
          }:${chunkConfigs[k]?.to || ""}`
      )
      .join(",");
  }, [chunkConfigs]);

  return useMemo(() => {
    // For brick layers (visualization mode "layers"), show count-based groups even without groupColumnName
    const isBrickLayerMode = visualizationMode === "layers" && visualizationLayerType;
    
    // Check if we have unified groups first (preferred)
    // This includes count-based groups for brick layers
    if (unifiedGroups && unifiedGroups.groups.length > 0) {
      // If in brick layer mode, always show unified groups (count-based)
      // Otherwise, only show if groupColumnName is set (column-based)
      if (isBrickLayerMode || groupColumnName) {
        return unifiedGroups.groups.map((group) => ({
          type: group.type,
          id: group.id,
          label: group.label,
          values: group.values,
          color: group.color,
          shape: group.shape,
          displayName: group.displayName,
          from: group.from,
          to: group.to,
        }));
      }
    }

    // For column-based grouping, require groupColumnName
    if (!groupColumnName) return [];

    // Fall back to legacy chunks/groups structure
    if (chunks && chunks.length > 0) {
      return chunks.map((chunk) => {
        const config = chunkConfigs?.[chunk.value] || null;
        const displayName =
          config?.displayName || chunk.displayName || chunk.label;
        let label = chunk.label;
        if (config?.from && config?.to) {
          label =
            config.from === config.to
              ? config.from
              : `${config.from}-${config.to}`;
        }
        const color = config?.color || chunk.color;

        return {
          type: "chunk" as const,
          id: chunk.value,
          label,
          values: chunk.values,
          color,
          shape: config?.shape,
          displayName,
          from: config?.from,
          to: config?.to,
        };
      });
    }

    // Return individual groups (non-chunked case)
    return uniqueValues.map((value: string, index: number) => {
      const config = groupConfigs?.[value];
      const defaultColor = groupColors[index % groupColors.length];
      return {
        type: "group" as const,
        id: value,
        label: value,
        values: [value], // Single value array for groups
        color: config?.color || defaultColor,
        shape: config?.shape,
        name: config?.name,
      };
    });
  }, [
    chunks,
    uniqueValues,
    groupColumnName,
    groupConfigsKey,
    chunkConfigsKey,
    chunkConfigs,
    groupConfigs,
    activeDataflowId,
    unifiedGroups,
  ]);
}
